import { addDays, daysBetween, endOfMonth, expectedDates } from '@fd/finance-core';
import { ArrowDownLeft, ArrowUpRight, PiggyBank, ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { today, useRecurring, type DashboardData, type RecurringItem } from '../../app/data.ts';
import { useT } from '../../app/i18n.tsx';
import { hrefFor, type Route } from '../../app/router.tsx';
import { usePeriod } from '../../shell/Shell.tsx';
import { Money } from '../../ui/primitives.tsx';
import { Sparkline } from '../../ui/Sparkline.tsx';
import { Delta } from '../../ui/Delta.tsx';

const SPENDABLE = new Set(['CURRENT', 'CASH']);

type SafeToSpend = { amountMinor: bigint; perDayMinor: bigint; until: string; nextPay: string | null; currency: string };

/**
 * What can still be spent this month without touching what is already spoken for: the money on the
 * current accounts, less the bills, subscriptions, repayments and savings transfers still due by the
 * month's end, plus the pay still to come in by then.
 */
function safeToSpend(data: DashboardData, recurring: readonly RecurringItem[], now: string): SafeToSpend {
  const currency = data.household.baseCurrency;
  const until = endOfMonth(now);
  const accounts = data.accounts.filter((a) => SPENDABLE.has(a.type) && !a.archivedAt && !a.isSavingsAsset && a.currency === currency);
  const ids = new Set(accounts.map((a) => a.id));
  let amount = accounts.reduce((sum, a) => sum + BigInt(a.balanceMinor), 0n);
  // A payment due in the last few days that the bank has not shown yet still leaves the account.
  const from = addDays(now, -3);
  for (const p of recurring) {
    if (!p.active || p.status === 'DISMISSED' || p.currency !== currency || !ids.has(p.accountId)) continue;
    // Money moved between two spendable accounts neither adds nor takes away.
    if (p.counterpartAccountId && ids.has(p.counterpartAccountId)) continue;
    const count = BigInt(expectedDates(p, until).filter((date) => date >= from).length);
    amount += (p.direction === 'IN' ? 1n : -1n) * p.typicalAmountMinor * count;
  }
  const days = BigInt(daysBetween(now, until) + 1);
  const nextPay =
    recurring
      .filter((p) => p.active && p.status !== 'DISMISSED' && p.direction === 'IN' && p.shownKind === 'INCOME' && p.nextDate >= now)
      .map((p) => p.nextDate)
      .sort()[0] ?? null;
  return { amountMinor: amount, perDayMinor: amount > 0n ? amount / days : 0n, until, nextPay, currency };
}

/** One figure of the month: the amount leads, the label and its context stay quiet. */
function MetricTile({
  tone,
  icon,
  label,
  value,
  sub,
  note,
  trend,
  href,
}: {
  tone: 'income' | 'expense' | 'savings' | 'accent';
  icon: ReactNode;
  label: string;
  value: ReactNode;
  sub: ReactNode;
  /** One short line under the amount (what of it still waits at the bank). */
  note?: ReactNode;
  trend?: readonly bigint[];
  href: string;
}) {
  return (
    <a className={`metric metric--${tone}`} href={href}>
      <span className="metric__head">
        <span className="metric__icon" aria-hidden="true">
          {icon}
        </span>
        <span className="metric__label">{label}</span>
      </span>
      <strong className="metric__value">{value}</strong>
      {note}
      <span className="metric__foot">
        <span className="metric__sub">{sub}</span>
        {trend && trend.length > 1 && (
          <span className="metric__spark">
            <Sparkline values={trend} color={`var(--fill-${tone === 'accent' ? 'savings' : tone})`} label={label} />
          </span>
        )}
      </span>
    </a>
  );
}

/** Income, spending and savings of the period against the previous one, and what is safe to spend. */
export function Kpis({ data }: { data: DashboardData }) {
  const t = useT();
  const period = usePeriod();
  const { items } = useRecurring();
  const now = today();
  const currency = data.household.baseCurrency;
  // Payments still pending at the bank count (the owner, 30.09.2026: that money is blocked already
  // — it may be booked or come back, and that is a difference): the figures include them, as the
  // cash-flow diagram does, and each says how much of it still waits at the bank.
  const totals = data.flow.booked;
  const settled = data.current.booked;
  const before = data.previous.booked;
  const safe = useMemo(() => safeToSpend(data, items, now), [data, items, now]);
  const vs = t.t(period.kind === 'WEEK' ? 'period.vsPreviousWeek' : period.kind === 'YEAR' ? 'period.vsPreviousYear' : 'period.vsPreviousMonth');
  const rate = totals.incomeMinor > 0n ? Number((totals.savingsContributionMinor * 1000n) / totals.incomeMinor) / 1000 : null;
  const pct = (value: number) => t.percent(value);
  const route = (r: Route) => hrefFor(r);
  const pending = (all: bigint, booked: bigint) =>
    all !== booked ? (
      <span className="metric__note amount-text" title={t.t('overview.pendingHint')}>
        {t.t('overview.pendingPart', { amount: t.money({ minor: all > booked ? all - booked : booked - all, currency }) })}
      </span>
    ) : null;

  return (
    <section className="metrics" aria-label={t.t('overview.kpis')}>
      <MetricTile
        tone="income"
        icon={<ArrowDownLeft />}
        label={t.t('dashboard.income')}
        value={<Money minor={totals.incomeMinor} currency={currency} animate />}
        sub={
          <>
            <Delta current={totals.incomeMinor} previous={before.incomeMinor} goodWhenUp />
            <span>{vs}</span>
          </>
        }
        note={pending(totals.incomeMinor, settled.incomeMinor)}
        trend={data.trend.income}
        href={route({ page: 'transactions', kind: 'income' })}
      />
      <MetricTile
        tone="expense"
        icon={<ArrowUpRight />}
        label={t.t('dashboard.expenses')}
        value={<Money minor={totals.netSpendMinor} currency={currency} animate />}
        sub={
          <>
            <Delta current={totals.netSpendMinor} previous={before.netSpendMinor} goodWhenUp={false} />
            <span>{vs}</span>
          </>
        }
        note={pending(totals.netSpendMinor, settled.netSpendMinor)}
        trend={data.trend.spending}
        href={route({ page: 'transactions', kind: 'expenses' })}
      />
      <MetricTile
        tone="savings"
        icon={<PiggyBank />}
        label={t.t('dashboard.saved')}
        value={<Money minor={totals.savingsContributionMinor} currency={currency} animate />}
        sub={rate !== null ? t.t('overview.ofIncome', { percent: pct(rate) }) : t.t('overview.noIncomeYet')}
        note={pending(totals.savingsContributionMinor, settled.savingsContributionMinor)}
        trend={data.trend.saved}
        href={route({ page: 'savings' })}
      />
      <MetricTile
        tone="accent"
        icon={<ShieldCheck />}
        label={t.t('overview.safeToSpend')}
        value={<Money minor={safe.amountMinor} currency={safe.currency} animate />}
        sub={
          <span className="amount-text" title={t.t('overview.safeHint')}>
            {[
              t.t('overview.until', { date: t.date(safe.until, 'dayMonth') }),
              safe.perDayMinor > 0n ? t.t('overview.perDay', { amount: t.money({ minor: safe.perDayMinor, currency: safe.currency }) }) : null,
              safe.nextPay ? t.t('overview.nextPay', { date: t.date(safe.nextPay, 'dayMonth') }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        }
        href={route({ page: 'transactions', view: 'upcoming' })}
      />
    </section>
  );
}
